<template>
	<div class="chat-page page-content">

		<div class="chat-container" :class="{ 'mobile-sidebar-open': isSidebarOpen }">
			<!-- 移动端侧边栏遮罩 -->
			<div 
				v-if="isMobile && isSidebarOpen" 
				class="sidebar-overlay"
				@click="closeSidebar"
			></div>

			<!-- 左侧历史记录 -->
			<div class="sidebar-wrapper" :class="{ 'sidebar-open': isSidebarOpen }">
				<div class="sidebar-header" v-if="isMobile">
					<h3 class="sidebar-title">聊天记录</h3>
					<button 
						class="sidebar-close-btn"
						@click="closeSidebar"
						aria-label="关闭侧边栏"
					>
						×
					</button>
				</div>
				<ChatHistory 
					:conversations="conversations" 
					:activeId="activeConversationId" 
					@select="selectConversation"
					@new="createNewConversation" 
					@delete="deleteConversation" 
				/>
			</div>

			<!-- 右侧对话区域 -->
			<div class="chat-main" ref="chat_main">
				<ChatHeader 
					:title="currentConversation ? currentConversation.title : '新对话'"
					:showMenuButton="isMobile"
					@clear="clearCurrentChat" 
					@toggleSidebar="toggleSidebar"
				/>

				<MessageList :messages="currentMessages" :loading="isLoading" />

				<ChatInput 
					ref="ChatInput"
					@updateChatInputFiles="updateChatInputFiles" 
					:disabled="isLoading" 
					@send="sendMessage" 
					@stop="stopGenerating" 
				/>
			</div>
		</div>

		<!-- 移动端浮动按钮 -->
		<button 
			v-if="isMobile" 
			class="fab-new-chat" 
			@click="createNewConversation"
			aria-label="新建对话"
		>
			+
		</button>
	</div>
</template>

<script>
	
	import ChatHistory from '@/components/chat/ChatHistory.vue'
	import ChatHeader from '@/components/chat/ChatHeader.vue'
	import MessageList from '@/components/chat/MessageList.vue'
	import ChatInput from '@/components/chat/ChatInput.vue'
	import {
		mapActions
	} from 'vuex'
	import {
		apiUrlNew
	} from '../../config/app.js'
	
	export default {
		name: 'AIChat',

		components: {
			ChatHistory,
			ChatHeader,
			MessageList,
			ChatInput
		},

		data() {
			return {
				userId:null,
				conversations: [],
				activeConversationId: null,
				messages: {},
				ChatInputFiles:[],
				isLoading: false,
				isMobile: false,
				isSidebarOpen: false,
				aiResponses: {
					'荷载': `钢结构荷载计算主要包括以下几个步骤：

**1. 恒载计算**
- 结构自重：钢材容重 78.5 kN/m³
- 建筑面层：根据实际材料确定
- 吊顶荷载：一般取 0.5-1.0 kN/m²

**2. 活载计算**
- 办公楼：2.0 kN/m²
- 教室：2.5 kN/m²
- 走廊：3.5 kN/m²
- 具体取值参考《建筑结构荷载规范》GB50009-2012

**3. 风荷载计算**
基本公式：W = βz·μs·μz·w0
- βz：高度z处的风振系数
- μs：风荷载体型系数
- μz：风压高度变化系数
- w0：基本风压

**4. 地震作用**
采用底部剪力法或振型分解反应谱法计算

**5. 荷载组合**
- 基本组合：1.2×恒载 + 1.4×活载
- 标准组合：恒载 + 活载
- 准永久组合：恒载 + 0.5×活载

需要我详细讲解某个具体的计算方法吗？`,

					'焊接': `钢结构焊接工艺要点包括：

**焊前准备**
1. **坡口加工**
   - V型坡口：板厚 6-20mm
   - X型坡口：板厚 >20mm
   - 坡口角度：60°-70°

2. **预热要求**
   - Q235钢：一般不需预热
   - Q345钢：板厚≥40mm时预热100-150℃
   - Q390钢：板厚≥30mm时预热100-150℃

**焊接参数**
1. **焊接电流**
   - φ3.2焊条：100-130A
   - φ4.0焊条：160-200A
   - φ5.0焊条：200-250A

2. **焊接电压**
   - 手工焊：22-28V
   - CO₂焊：24-32V

**焊接顺序**
1. 先焊收缩量大的焊缝
2. 先焊对接焊缝，后焊角焊缝
3. 对称施焊，减少变形

**质量控制**
- 外观检查：无裂纹、未熔合、夹渣
- 超声波探伤：一级焊缝100%检测
- 层间温度控制：≤250℃

还有其他焊接相关问题吗？`,

					'PPT': `我可以帮您快速生成钢结构课程的PPT课件！

**推荐使用一键备课功能**：
1. 🎯 进入"一键备课"页面
2. 📊 选择"PPT课件"资源
3. ⏱️ 3分钟自动生成专业课件

**PPT内容特色**：
- 📐 包含最新规范标准
- 🎨 专业的视觉设计
- 📹 配套动画演示
- 📝 完整的讲稿备注

**覆盖章节**：
- 第1章：钢结构材料
- 第2章：钢结构连接
- 第3章：轴心受力构件
- 第4章：受弯构件
- 第5章：压弯构件
- 第6章：钢结构设计

点击右下角的"⚡"按钮立即开始制作！`,

					'中建钢构': `中建钢构现场工程师项目是我校重点校企合作项目：

**项目概况**
🏢 合作企业：中国建筑钢结构有限公司
📍 实习地点：全国各大项目工地
⏰ 培养周期：6个月

**培养方向**
1. **施工现场管理**
   - 施工组织设计
   - 进度计划编制
   - 现场协调管理

2. **质量控制体系**
   - 焊接质量控制
   - 安装精度控制
   - 质量检验标准

3. **安全生产管理**
   - 安全技术交底
   - 危险源识别
   - 应急预案编制

4. **BIM技术应用**
   - Tekla建模
   - 碰撞检查
   - 4D施工模拟

**就业优势**
💼 签约率：85%以上
💰 起薪：8000-12000元/月
📈 晋升通道：技术员→工程师→项目经理

**申请条件**
- 钢结构相关专业大三学生
- 成绩排名前50%
- 通过企业面试

想了解具体申请流程吗？`
				}
			}
		},

		computed: {
			currentConversation() {
				return this.conversations.find(c => c.id === this.activeConversationId)
			},

			currentMessages() {
				return this.messages[this.activeConversationId] || []
			}
		},

		mounted() {
			this.getUserId()
			this.initializeChat()
			this.handleResize()
			window.addEventListener('resize', this.handleResize)

			// 检查是否有查询参数
			const query = this.$route.query.q
			if (query) {
				// 延迟发送，确保组件已经初始化
				this.$nextTick(() => {
					setTimeout(() => {
						this.sendMessage(query)
					}, 500)
				})
			}
		},

		beforeDestroy() {
			window.removeEventListener('resize', this.handleResize)
		},

		methods: {
			...mapActions(['showToast']),
			
			getUserId(){
				console.log('getUserId')
				let currentUser=sessionStorage.getItem('currentUser')
				try{
					currentUser=JSON.parse(currentUser)
					console.log(currentUser)
					if (currentUser?.user?.userId){
						this.userId=currentUser.user.userId
						console.log(`获取到用户ID:${this.userId}`)
					}
				}catch(e){
					console.error('获取登录信息异常')
				}
				
			},

			handleResize() {
				this.isMobile = window.innerWidth <= 768
				// 桌面端自动关闭侧边栏
				if (!this.isMobile) {
					this.isSidebarOpen = false
				}
			},

			toggleSidebar() {
				this.isSidebarOpen = !this.isSidebarOpen
			},

			closeSidebar() {
				this.isSidebarOpen = false
			},

			updateChatInputFiles(files){
				this.ChatInputFiles=files
			},
			
			initializeChat() {
				// 从localStorage加载历史对话
				const saved = localStorage.getItem('chat_conversations')
				if (saved) {
					try {
						const data = JSON.parse(saved)
						this.conversations = data.conversations || []
						this.messages = data.messages || {}
					} catch (e) {
						console.error('Failed to load chat history:', e)
					}
				}

				// 创建新对话或选择最近的对话
				if (this.conversations.length === 0) {
					this.createNewConversation()
				} else {
					this.activeConversationId = this.conversations[0].id
				}
			},

			saveToLocalStorage() {
				const data = {
					conversations: this.conversations,
					messages: this.messages
				}
				localStorage.setItem('chat_conversations', JSON.stringify(data))
			},

			createNewConversation() {
				const newConversation = {
					id: `conv_${Date.now()}`,
					title: '新对话',
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString()
				}

				this.conversations.unshift(newConversation)
				this.activeConversationId = newConversation.id
				this.$set(this.messages, newConversation.id, [])
				this.saveToLocalStorage()

				// 移动端新建对话后关闭侧边栏
				if (this.isMobile) {
					this.closeSidebar()
				}
			},

			selectConversation(id) {
				this.activeConversationId = id
				// 移动端选择对话后关闭侧边栏
				if (this.isMobile) {
					this.closeSidebar()
				}
			},

			deleteConversation(id) {
				const index = this.conversations.findIndex(c => c.id === id)
				if (index > -1) {
					this.conversations.splice(index, 1)
					delete this.messages[id]

					if (this.activeConversationId === id) {
						if (this.conversations.length > 0) {
							this.activeConversationId = this.conversations[0].id
						} else {
							this.createNewConversation()
						}
					}

					this.saveToLocalStorage()
				}
			},

			clearCurrentChat() {
				console.log("清空对话")
				localStorage.removeItem('chat_conversations');
				if (this.activeConversationId) {
					this.$set(this.messages, this.activeConversationId, [])
					this.saveToLocalStorage()
					this.showToast('对话已清空')
				}
			},

			scrollToBottom(){
				try{
					setTimeout(()=>{
						const messageList = this.$refs.chat_main.querySelector('.message-list');
						if (messageList) {
						  messageList.scrollTop = messageList.scrollHeight;
						}	
					},100)
					
				}catch(e){}
				
			},
			async setChatFinishToken(){
				console.log('setChatFinishToken')
				if (this.userId) {
					this.HttpUtils.get(`/api/wxmini/token/getCountFlowBycost_details/${this.userId}`, {
						
					}).then(res => {
						console.log(res)
						// if (res.data.code === 200) {
						// } else {
						// 	this.$message.error(res.data.msg)
						// }
					})
				}
			},

			async sendMessage(content) {
				// this.setChatFinishToken();return;
				if (!content.trim() || this.isLoading) return

				const conversationId = this.activeConversationId
				const messages = this.messages[conversationId] || []

				// 添加用户消息
				const userMessage = {
					id: `msg_${Date.now()}`,
					role: 'user',
					content: content,
					timestamp: new Date().toISOString()
				}
				messages.push(userMessage)
				this.$set(this.messages, conversationId, messages)

				// 更新对话标题（如果是第一条消息）
				if (messages.length === 1) {
					const conversation = this.conversations.find(c => c.id === conversationId)
					if (conversation) {
						conversation.title = content.slice(0, 20) + (content.length > 20 ? '...' : '')
						conversation.updatedAt = new Date().toISOString()
					}
				}

				// 开始生成回复
				this.isLoading = true
				const testData = {
					"query": content.trim(),
					"history": [],
					"meta": {
						"stream": true,
						"summary_title": true,
						"enable_retrieval": true,
						"history_round": 10,
						"selectedKB": 18,
						"use_graph": false,
						"db_name": "autokb",
						"show_assistant": true,
						"user_id": this.userId ? this.userId : 1,
					},
					"cur_res_id":  'res_' + Date.now() + 1,
				}
				
				//如果没有上传附件 不使用meta.db_name
				const uploadedFiles=this.$refs['ChatInput'].getUploadedFiles();
				console.log('UploadedFiles',uploadedFiles)
				try{
					if(!uploadedFiles?.length){
						testData.meta.db_name='t54b1330a'
					}
				}catch(e){}
				
				
				//http://192.168.20.50:5173/api/chat/ http://61.169.115.205:5173/api/chat/  https://cs.1andx.com/api/api/integrated/ai/chat	https://cs.1andx.com/api2/api/chat/
				fetch(`${apiUrlNew}/chat/`, {
						method: 'POST',
						body: JSON.stringify(testData),
						headers: {
							'Content-Type': 'application/json',
							'Authorization': `Bearer ${sessionStorage.getItem('token')}`
						},
						// body: JSON.stringify({
						// 	query: content.trim(),
						// 	history: [],
						// 	openid: "oixsJ7pZo5T2vrpyFiYsCSgihedc",
						// 	meta: {
						// 		enable_retrieval: true,
						// 		use_graph: false,
						// 		use_web: false,
						// 		graph_name: "neo4j",
						// 		rewriteQuery: "off",
						// 		selectedKB: 0,
						// 		stream: true,  // 启用流式响应
						// 		summary_title: true,
						// 		history_round: 5,
						// 		db_name: "kc21f969b"
						// 	},
						// 	cur_res_id: "lBE71nc0pSkVMotT",
						// }),
						// 	headers: {
						// 	  'Content-Type': 'application/json',
						// 	  'Authorization': `Bearer ${sessionStorage.getItem('token')}`
						// 	}
						// 

					})
					.then(response => {
						// console.log('fetch response')
						// console.log(response)
						//if (response.data.code === 200) {
							// 创建AI消息
							const aiMessage = {
								id: `msg_${Date.now()}`,
								role: 'assistant',
								content: '',
								timestamp: new Date().toISOString()
							}
							messages.push(aiMessage)
							this.$set(this.messages, conversationId, messages)

							// 处理流式响应
							const reader = response.body.getReader();
							const decoder = new TextDecoder("utf-8");
							let buffer = ''

							const readChunk = () => {
								return reader.read().then(({
									done,
									value
								}) => {
									if (done) {
										// 流结束
										// console.log('流结束')
										let lines = buffer.split('\n');
										let line = lines[lines.length-1].trim();
										 // console.log('line流结束',line)
										try{
											
											let thisData=JSON.parse(line)
											// console.log('-------------thisData')
											// console.log(typeof(thisData))
											// console.log(thisData)
											if(thisData.response){
												aiMessage.content = thisData.response
												this.$set(this.messages, conversationId, messages)
												this.scrollToBottom();
											}
											
										}catch(e){
											console.log(e)
										}
										
										this.isLoading = false
										this.setChatFinishToken();
										// aiMessage.content = buffer
										// this.$set(this.messages, conversationId, messages)
										// this.isLoading = false
										// this.saveToLocalStorage()

										// // 更新对话时间
										// const conversation = this.conversations.find(c => c.id ===
										// 	conversationId)
										// if (conversation) {
										// 	conversation.updatedAt = new Date().toISOString()
										// }

										return
									}

									// 处理接收到的数据
									buffer += decoder.decode(value, { stream: true });
									// aiMessage.content = buffer
									// console.log('buffer',buffer)
									// console.log(typeof(buffer))
									 let lines = buffer.split('\n');
									 let line = lines[lines.length-2].trim();
									 // console.log('line',line)
									try{
										
										let thisData=JSON.parse(line)
										// console.log('-------------thisData')
										// console.log(typeof(thisData))
										// console.log(thisData)
										if(thisData.response){
											aiMessage.content = thisData.response
											this.$set(this.messages, conversationId, messages)
											this.scrollToBottom();
										}
										
									}catch(e){
										// console.log(e)
									}
									// this.$set(this.messages, conversationId, messages)

									// 继续读取
									return readChunk()
								})
							}

							readChunk()
						// } else {
						// 	this.$message.error(response.data.msg)
						// }
					}).catch(error => {
						this.$message.error('请求失败: ' + error.message)
						this.isLoading = false
					})
			},

			getAIResponse(query) {
				// 关键词匹配
				const keywords = Object.keys(this.aiResponses)
				for (const keyword of keywords) {
					if (query.includes(keyword)) {
						return this.aiResponses[keyword]
					}
				}

				// 默认回复
				return `我理解您想了解"${query}"。作为您的建筑钢结构专业AI助手，我可以帮助您：

📐 **结构设计计算**
- 荷载计算与组合
- 构件强度验算
- 稳定性分析
- 连接节点设计

📚 **教学资源生成**
- PPT课件制作
- 教案设计
- 实训指导书
- 考核方案

🔧 **施工技术指导**
- 焊接工艺参数
- 安装施工方案
- 质量控制要点
- 安全技术措施

📋 **规范标准查询**
- GB50017-2017 钢结构设计标准
- GB50205-2020 钢结构工程施工质量验收规范
- GB50661-2011 钢结构焊接规范

请问您具体需要哪方面的帮助？`
			},

			stopGenerating() {
				this.isLoading = false
				this.showToast('已停止生成')
			}
		}
	}
</script>

<style scoped>
	/* 基础布局 */
	.chat-page {
		height: 100vh;
		display: flex;
		flex-direction: column;
		background: linear-gradient(135deg, #F5F5F7 0%, #E8F3FF 100%);
		overflow: hidden;
	}

	.chat-container {
		flex: 1;
		display: flex;
		overflow: hidden;
		position: relative;
	}

	.chat-main {
		flex: 1;
		display: flex;
		flex-direction: column;
		background: white;
		min-width: 0;
		position: relative;
		height: 100%;
	}

	/* 确保输入框在移动端可见 */
	@media (max-width: 768px) {
		.chat-main {
			height: calc(100vh - 64px); /* 减去导航栏高度 */
			padding-bottom: env(safe-area-inset-bottom, 0);
		}
		
		.chat-container {
			height: calc(100vh - 64px); /* 减去导航栏高度 */
		}
	}

	/* 侧边栏样式 */
	.sidebar-wrapper {
		width: 300px;
		background: white;
		border-right: 1px solid #e0e6ed;
		display: flex;
		flex-direction: column;
		transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		z-index: 100;
		flex-shrink: 0;
	}

	.sidebar-header {
		display: none;
		align-items: center;
		justify-content: space-between;
		padding: 16px 20px;
		border-bottom: 1px solid #e0e6ed;
		background: #fafbfc;
		flex-shrink: 0;
	}

	.sidebar-title {
		margin: 0;
		font-size: 16px;
		font-weight: 600;
		color: #2c3e50;
	}

	.sidebar-close-btn {
		background: none;
		border: none;
		font-size: 24px;
		color: #666;
		cursor: pointer;
		padding: 8px;
		border-radius: 50%;
		transition: all 0.2s;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.sidebar-close-btn:hover {
		background: rgba(0, 122, 255, 0.1);
		color: var(--primary);
	}

	.sidebar-close-btn:active {
		transform: scale(0.95);
	}

	/* 侧边栏遮罩 */
	.sidebar-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.5);
		z-index: 99;
		opacity: 0;
		animation: fadeIn 0.3s ease forwards;
	}

	/* 移动端遮罩定位修正 */
	@media (max-width: 768px) {
		.sidebar-overlay {
			top: 64px; /* 从导航栏下方开始 */
			height: calc(100vh - 64px);
		}
	}

	@media (max-width: 480px) {
		.sidebar-overlay {
			top: 60px;
			height: calc(100vh - 60px);
		}
	}

	/* 浮动按钮 */
	.fab-new-chat {
		position: fixed;
		bottom: 200px; /* 大幅增加底部距离 */
		right: 20px;
		width: 56px;
		height: 56px;
		border-radius: 50%;
		background: linear-gradient(135deg, #007AFF, #5856D6);
		color: white;
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 24px;
		cursor: pointer;
		box-shadow: 0 8px 20px rgba(0, 122, 255, 0.3);
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		font-weight: 500;
		z-index: 100;
	}

	.fab-new-chat:hover {
		transform: translateY(-50%) translateY(-2px) scale(1.05);
		box-shadow: 0 12px 28px rgba(0, 122, 255, 0.4);
	}

	.fab-new-chat:active {
		transform: translateY(-50%) scale(0.95);
	}

	/* 桌面端浮动按钮样式 */
	@media (min-width: 769px) {
		.fab-new-chat {
			bottom: 200px !important;
			top: auto !important;
			transform: none !important;
		}

		.fab-new-chat:hover {
			transform: translateY(-2px) scale(1.05);
		}

		.fab-new-chat:active {
			transform: scale(0.95);
		}
	}

	/* 平板设备优化 (768px - 1024px) */
	@media (max-width: 1024px) and (min-width: 769px) {
		.sidebar-wrapper {
			width: 280px;
		}
	}

	/* 移动端优化 (≤768px) */
	@media (max-width: 768px) {
		.chat-container {
			position: relative;
			height: calc(100vh - 64px); /* 减去导航栏高度 */
		}

		.sidebar-wrapper {
			position: fixed;
			top: 64px; /* 从导航栏下方开始 */
			left: 0;
			height: calc(100vh - 64px);
			width: 320px;
			max-width: 85vw;
			transform: translateX(-100%);
			z-index: 101;
			box-shadow: none;
		}

		.sidebar-wrapper.sidebar-open {
			transform: translateX(0);
			box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
		}

		.sidebar-header {
			display: flex !important;
		}

		.chat-main {
			width: 100%;
			height: calc(100vh - 64px);
			position: relative;
		}

		/* 确保输入框在最底部且可见 */
		.chat-main > :last-child {
			position: sticky;
			bottom: 0;
			z-index: 50;
			background: white;
			border-top: 1px solid #e0e6ed;
		}

		.fab-new-chat {
			bottom: auto; /* 改变定位方式 */
			top: 50%; /* 移动到屏幕中央 */
			transform: translateY(-50%);
			right: 16px;
			width: 52px;
			height: 52px;
			font-size: 22px;
			box-shadow: 0 6px 16px rgba(0, 122, 255, 0.3);
		}
	}

	/* 小屏手机优化 (≤480px) */
	@media (max-width: 480px) {
		.chat-container {
			height: calc(100vh - 60px); /* 调整为小屏导航栏高度 */
		}

		.sidebar-wrapper {
			top: 60px;
			height: calc(100vh - 60px);
			width: 280px;
			max-width: 90vw;
		}

		.chat-main {
			height: calc(100vh - 60px);
		}

		.sidebar-header {
			padding: 12px 16px;
		}

		.sidebar-title {
			font-size: 14px;
		}

		.sidebar-close-btn {
			width: 36px;
			height: 36px;
			font-size: 20px;
		}

		.fab-new-chat {
			width: 48px;
			height: 48px;
			font-size: 20px;
			bottom: auto;
			top: 50%;
			transform: translateY(-50%);
			right: 12px;
		}

		/* 确保输入框可见 */
		.chat-main > :last-child {
			position: sticky;
			bottom: 0;
			z-index: 50;
			background: white;
			box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
		}
	}

	/* 超小屏优化 (≤360px) */
	@media (max-width: 360px) {
		.sidebar-wrapper {
			width: 260px;
			max-width: 95vw;
		}

		.sidebar-header {
			padding: 10px 12px;
		}

		.sidebar-title {
			font-size: 13px;
		}
	}

	/* 横屏模式优化 */
	@media (max-height: 500px) and (orientation: landscape) {
		.chat-page {
			height: 100vh;
		}

		.sidebar-header {
			padding: 8px 16px;
		}

		.sidebar-title {
			font-size: 14px;
		}

		.fab-new-chat {
			bottom: auto;
			top: 50%;
			transform: translateY(-50%);
			width: 44px;
			height: 44px;
			font-size: 18px;
		}

		/* 确保变量定义 */
		:root {
			--primary: #007AFF;
		}
	}

	/* 超宽屏优化 (≥1440px) */
	@media (min-width: 1440px) {
		.sidebar-wrapper {
			width: 360px;
		}
	}

	/* 高DPI屏幕优化 */
	@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
		.fab-new-chat {
			box-shadow: 0 4px 12px rgba(0, 122, 255, 0.25);
		}
		
		.sidebar-wrapper.sidebar-open {
			box-shadow: 2px 0 12px rgba(0, 0, 0, 0.1);
		}
	}

	/* 触摸设备优化 */
	@media (hover: none) and (pointer: coarse) {
		.sidebar-close-btn:hover {
			background: none;
			color: #666;
		}

		.sidebar-close-btn:active {
			background: rgba(0, 122, 255, 0.1);
			color: var(--primary);
		}

		.fab-new-chat:hover {
			transform: none;
			box-shadow: 0 8px 20px rgba(0, 122, 255, 0.3);
		}

		.fab-new-chat:active {
			transform: scale(0.95);
			box-shadow: 0 6px 16px rgba(0, 122, 255, 0.4);
		}
	}

	/* 无障碍优化 */
	@media (prefers-reduced-motion: reduce) {
		.sidebar-wrapper,
		.fab-new-chat,
		.sidebar-overlay {
			transition: none;
			animation: none;
		}
	}

	/* 深色模式支持 */
	@media (prefers-color-scheme: dark) {
		.chat-page {
			background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
		}

		.sidebar-wrapper {
			background: #2d3748;
			border-right-color: #4a5568;
		}

		.sidebar-header {
			background: #374151;
			border-bottom-color: #4a5568;
		}

		.sidebar-title {
			color: #f7fafc;
		}

		.sidebar-close-btn {
			color: #cbd5e0;
		}

		.sidebar-close-btn:hover {
			background: #4a5568;
		}

		.chat-main {
			background: #2d3748;
		}

		.sidebar-overlay {
			background: rgba(0, 0, 0, 0.7);
		}
	}

	/* 动画 */
	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	/* 打印样式 */
	@media print {
		.sidebar-wrapper,
		.fab-new-chat,
		.sidebar-overlay {
			display: none !important;
		}

		.chat-page {
			height: auto;
			background: white;
		}

		.chat-main {
			height: auto;
		}
	}



	/* 折叠屏设备优化 */
	@media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
		.sidebar-wrapper {
			width: 300px;
		}
	}

	@media (min-width: 1024px) and (max-width: 1366px) and (orientation: landscape) {
		.sidebar-wrapper {
			width: 280px;
		}
	}
</style>